import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { post } from "@common/api/apiShared";
import { useDotLegalMutation, useDotLegalQuery } from "@common/hooks/dotLegalReactQuery";
import { useSelectableEntityTemplatesForCreation } from "@common/hooks/useSelectableItemQueries";
import { useValidator } from "@common/hooks/validation";
import { ValidationError } from "@common/validationError";
import { usePermissionsProvider } from "../../auth/usePermissionsProvider";
import { EntityTemplate, ProcessStandardField, TemplateEntityType } from "../../entityTemplates/EntityTemplate.types";
import { hasFieldData } from "../../entityTemplates/fieldValue";
import { ProcessFieldModel } from "../../entityTemplates/ProcessFieldRenderers";
import { groupFieldsByTemplateStep } from "../../entityTemplates/templateStepFields/groupFieldsByTemplateStep";
import { useEntityTemplateLockedText } from "../../entityTemplates/useEntityTemplateLockedText";
import { useUrlProvider } from "../../useUrlProvider";
import { usePane } from "../../main/panes/paneLayout/usePane";
import { getProcessFieldName, getProcessFieldValue, ProcessRelatedValues } from "../editProcess/EditProcessValidator.hooks";
import { AddProcessSaveModel, CreateProcessModel } from "./AddProcessPane.types";

// Documents and excluded users are saved against the process id, and creation defaults the responsible
// and group companies. The template editor does not let them be required at creation, so the pane skips them should a
// template still say they are.
const fieldsSetAfterCreation: ReadonlySet<number> = new Set([
    ProcessStandardField.ProcessDocuments,
    ProcessStandardField.ProcessExcludedUsers,
    ProcessStandardField.ProcessResponsible,
    ProcessStandardField.ProcessGroupCompanies,
]);

function createEmptyProcess(): ProcessFieldModel {
    return {
        id: "",
        businessAreaIds: [],
        complianceAreaIds: [],
        assetIds: [],
        legalEntityIds: [],
        processingActivityIds: [],
        customFieldValues: [],
        excludedUsers: [],
        additionalAccessUsers: [],
        visibleToAllUsers: false,
        accessBusinessAreas: [],
        allGroupEntityAccess: true,
        groupEntities: [],
    };
}

export function useAddProcessPane() {
    const { translateString } = useTranslation();
    const { getLockedTextsById } = useEntityTemplateLockedText();
    const snackbar = useDotLegalSnackbar();
    const navigate = useNavigate();
    const { getProcessEditUrl } = useUrlProvider();
    const { close, setHasUnsavedChanges } = usePane();
    const { hasTagsAccess } = usePermissionsProvider();

    const [saveModel, setSaveModel] = useState<AddProcessSaveModel>({ name: "", entityTemplateId: "" });
    const [editedProcess, setEditedProcess] = useState<ProcessFieldModel>(createEmptyProcess());
    const [selectedTagIds, setSelectedTagIds] = useState<Array<string>>([]);
    const [showRequiredFieldErrors, setShowRequiredFieldErrors] = useState(false);

    const { data: entityTemplatesData, isLoading: templatesIsLoading } = useSelectableEntityTemplatesForCreation(TemplateEntityType.Process, false);
    const templates = (entityTemplatesData ?? []).map((x) => ({ id: x.id, name: x.name, disabled: !x.hasAccess }));
    const lockedTemplateTexts = getLockedTextsById(entityTemplatesData ?? []);

    const selectedTemplate = entityTemplatesData?.find((t) => t.id === saveModel.entityTemplateId);
    // The full template carries no such flag: the selectable view model is what reports that the user is barred from
    // a field this template insists on at creation time.
    const selectedTemplateMissingAccess = selectedTemplate?.missingRequiredAtCreationFieldAccess ?? false;

    const templateQuery = useDotLegalQuery<EntityTemplate>(`/entity-templates/${saveModel.entityTemplateId}`, { enabled: !!saveModel.entityTemplateId });
    const template = templateQuery.data;

    const requiredAtCreationFields = (template?.steps.flatMap((step) => step.fields) ?? []).filter(
        (field) =>
            field.requiredAtCreation &&
            !(field.standardFieldIdentifier && fieldsSetAfterCreation.has(field.standardFieldIdentifier)) &&
            (field.standardFieldIdentifier !== ProcessStandardField.ProcessTags || hasTagsAccess)
    );

    const fieldGroups = groupFieldsByTemplateStep(template, requiredAtCreationFields);

    const validation = useValidator<AddProcessSaveModel>(
        (validators) => ({
            name: validators.validateNotEmpty((item) => item.name, translateString("name")),
            entityTemplateId: validators.validateNotEmpty((item) => item.entityTemplateId, translateString("processTemplate")),
        }),
        []
    );

    validation.check(saveModel);

    // Locked templates are listed but cannot be chosen, so the only unlocked one is as good as the only one
    useEffect(() => {
        const unlockedTemplates = entityTemplatesData?.filter((x) => x.hasAccess);
        if (unlockedTemplates?.length === 1 && !saveModel.entityTemplateId) {
            setSaveModel((current) => ({ ...current, entityTemplateId: unlockedTemplates[0].id }));
        }
    }, [entityTemplatesData, saveModel.entityTemplateId]);

    const validateRequiredAtCreationFields = (): Array<ValidationError> => {
        const relatedValues: ProcessRelatedValues = { selectedTags: selectedTagIds, documentIds: [] };

        return requiredAtCreationFields
            .filter((field) => field.standardFieldIdentifier !== ProcessStandardField.ProcessName)
            .filter((field) => !hasFieldData(getProcessFieldValue(field, editedProcess, relatedValues)))
            .map((field) => new ValidationError(getProcessFieldName(field), translateString("requiredField")));
    };

    const requiredFieldValidationErrors = showRequiredFieldErrors ? validateRequiredAtCreationFields() : [];

    const modelUpdater = {
        onNameChange: (name: string) => updateModel({ name: name }),
        onTemplateChange: (entityTemplateId: string | null) => {
            updateModel({ entityTemplateId: entityTemplateId ?? "" });
            setEditedProcess(createEmptyProcess());
            setSelectedTagIds([]);
            setShowRequiredFieldErrors(false);
        },
        onTemplateFieldChange: async (changeFunc: (process: ProcessFieldModel) => void) => {
            setHasUnsavedChanges(true);
            setEditedProcess((current) => {
                const next = { ...current };
                changeFunc(next);
                return next;
            });
        },
        onTagsChange: (tagIds: Array<string>) => {
            setHasUnsavedChanges(true);
            setSelectedTagIds(tagIds);
        },
    };

    function updateModel(changes: Partial<AddProcessSaveModel>) {
        setHasUnsavedChanges(true);
        setSaveModel((current) => ({ ...current, ...changes }));
    }

    const saveMutation = useDotLegalMutation((model: CreateProcessModel) => post<{ id: string }>("/processes", model));

    function onSaveClick() {
        const requiredFieldErrors = validateRequiredAtCreationFields();

        if (validation.anyHasErrors) {
            validation.setShowErrors(true);
        }

        if (requiredFieldErrors.length > 0) {
            setShowRequiredFieldErrors(true);
        }

        if (validation.anyHasErrors || requiredFieldErrors.length > 0) {
            return;
        }

        saveMutation.mutateAsync(mapToCreateModel(), {
            onSuccess: (response) => {
                snackbar.show(translateString("processCreated"));
                // A pane outlives a navigation, so it has to close itself or it stays open over the new process
                close();
                // The steps open on a brand new process, so they must not flag missing required fields before the
                // user has had a chance to reach them.
                navigate(getProcessEditUrl(response.value().id, "create=true"));
            },
        });
    }

    function mapToCreateModel(): CreateProcessModel {
        const { id, status, responsibleId, allGroupEntityAccess, excludedUsers, groupEntities, ...collectedFields } = editedProcess;

        return {
            ...collectedFields,
            name: saveModel.name,
            entityTemplateId: saveModel.entityTemplateId,
            tagIds: selectedTagIds,
        };
    }

    return {
        saveModel,
        modelUpdater,
        onSaveClick,
        templates,
        templatesIsLoading,
        lockedTemplateTexts,
        isLoadingTemplate: templateQuery.isLoading && !!saveModel.entityTemplateId,
        selectedTemplateMissingAccess,
        fieldGroups,
        editedProcess,
        selectedTagIds,
        requiredFieldValidationErrors,
        validation,
        isSaving: saveMutation.isLoading,
    };
}
